export interface MediaLike {
  currentTime: number;
  duration: number;
  paused: boolean;
  muted: boolean;
  play(): Promise<void> | void;
  pause(): void;
}

export const DRIFT_TOLERANCE = 0.15;

function finite(n: number): number {
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Единая шкала времени для двух дорожек: эталон звучит в момент p,
 * исполнение — в момент p - offset (положительный сдвиг задерживает исполнение).
 */
export class SyncController {
  private position = 0;
  private startClock = 0;
  private startPosition = 0;
  playing = false;

  constructor(
    private readonly reference: MediaLike,
    private readonly attempt: MediaLike,
    public offset = 0,
    private readonly now: () => number = () => performance.now(),
  ) {}

  get length(): number {
    return Math.max(finite(this.reference.duration), finite(this.attempt.duration) + this.offset, 0);
  }

  get currentPosition(): number {
    return this.playing ? this.startPosition + (this.now() - this.startClock) / 1000 : this.position;
  }

  play() {
    if (this.playing) return;
    if (this.position >= this.length && this.length > 0) this.position = 0;
    this.startPosition = this.position;
    this.startClock = this.now();
    this.playing = true;
    this.tick();
  }

  pause() {
    if (!this.playing) return;
    this.position = this.currentPosition;
    this.playing = false;
    this.reference.pause();
    this.attempt.pause();
  }

  seek(position: number) {
    const clamped = Math.min(Math.max(0, position), this.length || position);
    this.position = clamped;
    if (this.playing) {
      this.startPosition = clamped;
      this.startClock = this.now();
    }
    this.align(this.reference, clamped, this.playing);
    this.align(this.attempt, clamped - this.offset, this.playing);
  }

  restart() {
    this.seek(0);
  }

  setOffset(offset: number) {
    const p = this.currentPosition;
    this.offset = offset;
    this.seek(p);
  }

  tick(): number {
    const p = this.currentPosition;
    if (this.playing && this.length > 0 && p >= this.length) {
      this.pause();
      this.position = this.length;
      return this.position;
    }
    this.align(this.reference, p, this.playing);
    this.align(this.attempt, p - this.offset, this.playing);
    return p;
  }

  private align(media: MediaLike, target: number, shouldPlay: boolean) {
    const duration = finite(media.duration);
    const inRange = target >= 0 && (duration === 0 || target < duration);
    if (!shouldPlay || !inRange) {
      if (!media.paused) media.pause();
      if (target < 0) media.currentTime = 0;
      else if (duration && target >= duration) media.currentTime = duration;
      else media.currentTime = target;
      return;
    }
    if (media.paused) {
      media.currentTime = target;
      void Promise.resolve(media.play()).catch(() => undefined);
    } else if (Math.abs(media.currentTime - target) > DRIFT_TOLERANCE) {
      media.currentTime = target;
    }
  }
}
